import { createAsyncThunk, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import _ from 'lodash';
import Constants from '../constants';
import { SurveyTheme, Theme } from '../models';
import { assertNonNullable } from '../utilities/assertions';
import { IApplicationState } from './index';

type ValidationResult<TKey extends string> = {
	isValid: boolean;
	failures: ValidationFailures<TKey>;
};

type ValidationFailures<TKey extends string> = {
	[key in TKey]?: string[];
};

type SurveyThemeValidatableKey = 'primaryButton' | 'secondaryButton';

type SurveyThemeValidationResult = ValidationResult<SurveyThemeValidatableKey>;

type SurveyThemeStatus = 'idle' | 'loading' | 'saving' | 'failed';

interface ISurveyThemeState {
	status: SurveyThemeStatus;
	dirty: boolean;
	surveyTheme?: SurveyTheme;
	originalSurveyTheme?: SurveyTheme;
	validationResult: SurveyThemeValidationResult;
}

const initialState: ISurveyThemeState = {
	status: 'idle',
	dirty: false,
	validationResult: { isValid: true, failures: {} },
};

const loadSurveyTheme = createAsyncThunk('surveyTheme/loadSurveyTheme', async (surveyId: number) => {
	const response = await axios.get<SurveyTheme>(Constants.SURVEY_THEME_API_URL.replace('{surveyId}', surveyId.toString()), {
		validateStatus: (status) => status === 200 || status === 404,
	});
	return response.status === 200 ? response.data : undefined;
});

const deleteSurveyTheme = async (surveyId: number) => {
	await axios.delete(Constants.SURVEY_THEME_API_URL.replace('{surveyId}', surveyId.toString()));
	return undefined;
};

const createSurveyTheme = async (surveyId: number, surveyTheme: SurveyTheme) => {
	const response = await axios.post<SurveyTheme>(Constants.SURVEY_THEME_API_URL.replace('{surveyId}', surveyId.toString()), surveyTheme);
	return response.data;
};

const updateSurveyTheme = async (surveyId: number, surveyTheme: SurveyTheme) => {
	const response = await axios.put<SurveyTheme>(Constants.SURVEY_THEME_API_URL.replace('{surveyId}', surveyId.toString()), surveyTheme);
	return response.data;
};

const saveSurveyTheme = createAsyncThunk('surveyTheme/saveSurveyTheme', async (surveyId: number, { getState }) => {
	const {
		surveyTheme: { dirty, surveyTheme, originalSurveyTheme },
	} = getState() as IApplicationState;

	if (!dirty) {
		return surveyTheme;
	}

	if (!surveyTheme) {
		return deleteSurveyTheme(surveyId);
	}

	return !originalSurveyTheme ? createSurveyTheme(surveyId, surveyTheme) : updateSurveyTheme(surveyId, surveyTheme);
});

const surveyThemeSlice = createSlice({
	name: 'surveyTheme',
	initialState: initialState,
	reducers: {
		setSurveyTheme: (state, action: PayloadAction<{ name: string; theme: Theme }>) => {
			const {
				payload: { name, theme },
			} = action;

			if (name === Constants.DEFAULT_THEME_NAME) {
				return {
					...state,
					dirty: !!state.originalSurveyTheme,
					surveyTheme: undefined,
					validationResult: { isValid: true, failures: {} },
				};
			}

			const surveyTheme = {
				...state.surveyTheme,
				name,
				...theme,
			};
			const dirty = !_.isEqual(state.originalSurveyTheme, surveyTheme);
			const validationResult = validateSurveyTheme(surveyTheme);

			return {
				...state,
				dirty,
				surveyTheme,
				validationResult,
			};
		},
		updateSurveyThemeValue: (state, action: PayloadAction<{ name: keyof SurveyTheme; value: string }>) => {
			assertNonNullable(state.surveyTheme);

			const {
				payload: { name, value },
			} = action;

			const surveyTheme = { ...state.surveyTheme, name: Constants.CUSTOM_THEME_NAME, [name]: value };
			const dirty = !_.isEqual(state.originalSurveyTheme, surveyTheme);
			const validationResult = validateSurveyTheme(surveyTheme);

			return {
				...state,
				dirty,
				surveyTheme,
				validationResult,
			};
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadSurveyTheme.pending, (state) => {
				state.status = 'loading';
			})
			.addCase(saveSurveyTheme.pending, (state) => {
				state.status = 'saving';
			})
			.addMatcher(isAnyOf(loadSurveyTheme.fulfilled, saveSurveyTheme.fulfilled), (state, action) => {
				state.status = 'idle';
				state.dirty = false;
				state.surveyTheme = action.payload;
				state.originalSurveyTheme = _.cloneDeep(action.payload);
				state.validationResult = { isValid: true, failures: {} };
			})
			.addMatcher(isAnyOf(loadSurveyTheme.rejected, saveSurveyTheme.rejected), (state) => {
				state.status = 'failed';
			});
	},
});

const validateSurveyTheme = ({
	primaryButtonColor,
	primaryButtonTextColor,
	secondaryButtonColor,
	secondaryButtonTextColor,
}: SurveyTheme): ValidationResult<SurveyThemeValidatableKey> => {
	const validationFailures: ValidationFailures<SurveyThemeValidatableKey> = {};

	if (primaryButtonColor.localeCompare(primaryButtonTextColor, 'en', { sensitivity: 'base' }) === 0) {
		validationFailures['primaryButton'] = ['Primary Button Color and Text Color must not be equal.'];
	}

	if (secondaryButtonColor.localeCompare(secondaryButtonTextColor, 'en', { sensitivity: 'base' }) === 0) {
		validationFailures['secondaryButton'] = ['Secondary Button Color and Text Color must not be equal.'];
	}

	return {
		isValid: Object.keys(validationFailures).length === 0,
		failures: validationFailures,
	};
};

export { type ISurveyThemeState, initialState, loadSurveyTheme, saveSurveyTheme, type SurveyThemeValidationResult };
export const { setSurveyTheme, updateSurveyThemeValue } = surveyThemeSlice.actions;
export default surveyThemeSlice.reducer;
